import React from 'react';
import { MenuItemStatus } from '../../types/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { comboSaving } from '../../utils/discountPricing';
import { formatPercent, formatTenge, formatTengeOff } from '../../utils/money';
import { fill } from '../../utils/template';
import { FormCard } from '../form/FormShell';
import { Field, FieldGrid, Suffixed } from '../form/Field';
import { TreeSelect } from '../TreeSelect';
import { COMBO_QUANTITY_MAX, COMBO_QUANTITY_MIN, ComboLine, parseWhole } from './discountDraft';
import { MenuGate } from './MenuGate';
import { DiscountCatalog } from './useDiscountCatalog';

/** Позиция, известная форме: из меню или из самой скидки. */
export interface KnownItem {
  id: number;
  name: string;
  price: number;
  status?: MenuItemStatus;
}

/** Строка состава: что выбрано и что о позиции известно. */
export interface ComboRow {
  line: ComboLine;
  item?: KnownItem;
}

interface ComboContentsCardProps {
  catalog: DiscountCatalog;
  rows: ComboRow[];
  /** Обычная цена набора; null — цены части позиций неизвестны. */
  regularPrice: number | null;
  error?: string;
  disabled: boolean;
  onItemsChange: (itemIds: number[]) => void;
  onQuantityChange: (menuItemId: number, quantity: number) => void;
  onRemove: (menuItemId: number) => void;
}

function CrossIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

/**
 * Состав комбо: позиции с количеством и обычная цена набора. Позиции
 * отмечаются в том же дереве меню, что у скидки на позиции.
 */
export function ComboContentsCard({
  catalog,
  rows,
  regularPrice,
  error,
  disabled,
  onItemsChange,
  onQuantityChange,
  onRemove,
}: ComboContentsCardProps) {
  const { t } = useLanguage();
  const strings = t.discounts;

  return (
    <FormCard title={strings.cardCombo} hint={strings.cardComboHint}>
      <div className="qp-form-group">
        {rows.length === 0 ? (
          <p className="qp-combo-empty">{strings.comboEmpty}</p>
        ) : (
          <div className="qp-combo">
            {rows.map(({ line, item }) => {
              const name = item?.name ?? `#${line.menuItemId}`;
              const hidden = item?.status != null && item.status !== 'active';

              return (
                <div key={line.menuItemId} className="qp-combo-line">
                  <span className="qp-cell-stack">
                    <span className="qp-cell-title">{name}</span>
                    <span className="qp-cell-sub">
                      {item ? fill(strings.comboUnitPrice, { price: formatTenge(item.price) }) : '—'}
                    </span>
                    {/* Гость не видит комбо со скрытой позицией (ADR-026 §3) —
                        партнёр должен узнать это здесь, а не от гостей. */}
                    {hidden && <span className="qp-combo-warn">{strings.comboHidden}</span>}
                  </span>

                  <span className="qp-stepper" role="group" aria-label={fill(strings.comboQuantity, { name })}>
                    <button
                      type="button"
                      onClick={() => onQuantityChange(line.menuItemId, line.quantity - 1)}
                      disabled={disabled || line.quantity <= COMBO_QUANTITY_MIN}
                      aria-label={fill(strings.comboLess, { name })}
                    >
                      −
                    </button>
                    <output aria-live="polite">{line.quantity}</output>
                    <button
                      type="button"
                      onClick={() => onQuantityChange(line.menuItemId, line.quantity + 1)}
                      disabled={disabled || line.quantity >= COMBO_QUANTITY_MAX}
                      aria-label={fill(strings.comboMore, { name })}
                    >
                      +
                    </button>
                  </span>

                  <span className="qp-combo-sum">{item ? formatTenge(item.price * line.quantity) : '—'}</span>

                  <button
                    type="button"
                    className="qp-btn qp-btn--icon qp-combo-remove"
                    onClick={() => onRemove(line.menuItemId)}
                    disabled={disabled}
                    aria-label={fill(strings.comboRemove, { name })}
                  >
                    <CrossIcon />
                  </button>
                </div>
              );
            })}

            <div className="qp-combo-total">
              <span>{strings.comboRegular}</span>
              <strong>{regularPrice != null ? formatTenge(regularPrice) : '—'}</strong>
            </div>
          </div>
        )}

        {error && (
          <span className="qp-field-error" role="alert">
            {error}
          </span>
        )}
      </div>

      <div className="qp-form-group">
        <span className="qp-field-label">{strings.comboPick}</span>
        <MenuGate catalog={catalog}>
          <TreeSelect
            categories={catalog.categories}
            items={catalog.items}
            mode="items"
            selectedItemIds={rows.map((row) => row.line.menuItemId)}
            onItemsChange={onItemsChange}
            disabled={disabled}
          />
        </MenuGate>
      </div>
    </FormCard>
  );
}

interface ComboPriceCardProps {
  bundlePrice: string;
  regularPrice: number | null;
  error?: string;
  disabled: boolean;
  onChange: (value: string) => void;
}

/** Цена комбо и итог: обычная цена, цена набора, выгода гостя в ₸ и %. */
export function ComboPriceCard({ bundlePrice, regularPrice, error, disabled, onChange }: ComboPriceCardProps) {
  const { t } = useLanguage();
  const strings = t.discounts;

  const bundle = parseWhole(bundlePrice);
  const saving = regularPrice != null && bundle != null ? comboSaving(regularPrice, bundle) : null;
  const noSaving = regularPrice != null && bundle != null && !saving;

  const savingText = saving
    ? `${formatTengeOff(saving.amount)} (${formatPercent(saving.percent)})`
    : noSaving
      ? strings.bundleNoSaving
      : '—';
  // Выгода — зелёным, её отсутствие — красным: это ошибка, сервер такое
  // комбо не примет.
  const savingTone = saving ? ' qp-fact-v--gain' : noSaving ? ' qp-fact-v--bad' : '';

  return (
    <FormCard title={strings.cardBundle} hint={strings.cardBundleHint}>
      <FieldGrid>
        <Field label={strings.bundle} required htmlFor="discount-bundle" error={error} hint={strings.bundleHint}>
          <Suffixed suffix="₸">
            <input
              id="discount-bundle"
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              value={bundlePrice}
              onChange={(event) => onChange(event.target.value)}
              className="qp-control qp-control--num"
              aria-invalid={!!error}
              disabled={disabled}
            />
          </Suffixed>
        </Field>
      </FieldGrid>

      <div className="qp-facts qp-facts--3">
        <div className="qp-fact">
          <span className="qp-fact-k">{strings.bundleRegular}</span>
          <span className="qp-fact-v qp-fact-v--num">
            {regularPrice != null ? formatTenge(regularPrice) : '—'}
          </span>
        </div>
        <div className="qp-fact">
          <span className="qp-fact-k">{strings.bundlePrice}</span>
          <span className="qp-fact-v qp-fact-v--num">{bundle != null ? formatTenge(bundle) : '—'}</span>
        </div>
        <div className="qp-fact">
          <span className="qp-fact-k">{strings.bundleSaving}</span>
          <span className={`qp-fact-v qp-fact-v--num${savingTone}`}>{savingText}</span>
        </div>
      </div>
    </FormCard>
  );
}
